import React, { useState, useEffect } from 'react';

const API_ENDPOINT = 'https://6f4o51pfc3.execute-api.eu-west-1.amazonaws.com/prod'; 

// Santa event date - December 12th, 2025
const EVENT_DATE = '2025-12-12';

function App() {
  const [email, setEmail] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [slotCounts, setSlotCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isChangingBooking, setIsChangingBooking] = useState(false);
  const [existingBooking, setExistingBooking] = useState(null);

  const generateTimeSlots = () => {
    const slots = [];
    // 6:00 PM to 8:00 PM in 15-minute intervals
    // Start at 18:00 (6:00 PM) and end at 20:00 (8:00 PM)
    
    // 5:30 PM
    //slots.push('17:30');
    // 5:45 PM
    //slots.push('17:45');
    
    // 6:00 PM to 7:45 PM
    for (let hour = 18; hour < 20; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
      }
    }
    
    // 8:00 PM
    slots.push('20:00');
    
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const formatEventDate = () => {
    const date = new Date(EVENT_DATE + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getSlotStatus = (count) => {
    if (count >= 50) {
      return { color: 'red', label: 'BUSY', bgClass: 'bg-red-500', borderClass: 'border-red-500', hoverClass: 'hover:bg-red-600' };
    } else if (count >= 20) {
      return { color: 'amber', label: 'BUSIER', bgClass: 'bg-amber-500', borderClass: 'border-amber-500', hoverClass: 'hover:bg-amber-600' };
    } else {
      return { color: 'green', label: 'LIGHT', bgClass: 'bg-green-500', borderClass: 'border-green-500', hoverClass: 'hover:bg-green-600' };
    }
  };

  useEffect(() => {
    fetchAvailableSlots();
  }, []);

  const fetchAvailableSlots = async () => {
    try {
      const response = await fetch(`${API_ENDPOINT}/slots?date=${EVENT_DATE}`);
      const data = await response.json();
      setSlotCounts(data.slotCounts || {});
    } catch (error) {
      console.error('Error fetching slots:', error);
      setSlotCounts({});
    }
  };

  const handleSubmit = async () => {
    if (!email || !selectedTime) {
      setMessage({ type: 'error', text: 'Please enter your email and select a time' });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // If changing booking, delete old one first
      if (isChangingBooking && existingBooking) {
        const deleteResponse = await fetch(`${API_ENDPOINT}/booking?email=${encodeURIComponent(email)}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!deleteResponse.ok) {
          setMessage({ 
            type: 'error', 
            text: 'Failed to update booking. Please try again.' 
          });
          setLoading(false);
          return;
        }
      }

      // Create new booking
      const response = await fetch(`${API_ENDPOINT}/book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          date: EVENT_DATE,
          time: selectedTime,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        const successText = isChangingBooking 
          ? '🎅 Ho ho ho! Your Santa visit time has been changed! Check your email for updated details.'
          : '🎅 Ho ho ho! Your Santa visit is confirmed! Check your email for details.';
        
        setMessage({ 
          type: 'success', 
          text: successText
        });
        setEmail('');
        setSelectedTime('');
        setIsChangingBooking(false);
        setExistingBooking(null);
        fetchAvailableSlots();
      } else if (response.status === 409) {
        // Email already has a booking
        if (data.booking && data.booking.time) {
          setExistingBooking(data.booking);
          const [hour, minute] = data.booking.time.split(':');
          const hourNum = parseInt(hour);
          const ampm = hourNum >= 12 ? 'PM' : 'AM';
          const displayHour = hourNum > 12 ? hourNum - 12 : hourNum === 0 ? 12 : hourNum;
          const displayTime = `${displayHour}:${minute} ${ampm}`;
          
          setIsChangingBooking(true);
          setMessage({ 
            type: 'error', 
            text: `You already have a booking at ${displayTime}. Click 'Change My Santa Visit' below to proceed.` 
          });
        } else {
          setIsChangingBooking(true);
          setMessage({ 
            type: 'error', 
            text: 'You already have a booking. Click \'Change My Santa Visit\' below to proceed.' 
          });
        }
      } else {
        setMessage({ 
          type: 'error', 
          text: data.message || 'Booking failed. Please try again.' 
        });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'Connection error. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-green-100 py-6 sm:py-12 px-3 sm:px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8">
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-red-700 mb-2">
              🎅 Santa Slot Picker
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mb-1">
              Choose your perfect time to meet Santa!
            </p>
            <p className="text-base sm:text-lg font-semibold text-green-700 mb-2">
              📅 {formatEventDate()}
            </p>
            <div className="text-xs sm:text-sm text-gray-700 space-y-1">
              <p className="font-semibold text-red-600">📍 Location:</p>
              <p className="font-medium">Round Towers Lusk GAA</p>
              <p>Thomas Ashe Park, Minister's Road</p>
              <p>Lusk, Co. Dublin, K45 PY79</p>
            </div>
          </div>

          {/* Important Information - Sticky Note Style */}
          <div className="mb-6 sm:mb-8 relative">
            {/* Red pushpin */}
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
              <div className="relative">
                <div className="w-8 h-8 bg-red-500 rounded-full shadow-lg"></div>
                <div className="absolute top-6 left-1/2 transform -translate-x-1/2 w-1 h-4 bg-red-600"></div>
                <div className="absolute top-3 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-white rounded-full opacity-40"></div>
              </div>
            </div>
            
            <div className="p-5 sm:p-7 rounded-sm" style={{ 
              backgroundColor: '#fef9c3',
              backgroundImage: 'linear-gradient(180deg, #fef9c3 0%, #fde68a 100%)',
              transform: 'rotate(-2deg)',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
              position: 'relative'
            }}>
              {/* Curled corner */}
              <div className="absolute bottom-0 right-0 w-0 h-0" style={{
                borderStyle: 'solid',
                borderWidth: '0 0 24px 24px',
                borderColor: 'transparent transparent #a3a3a3 transparent',
                filter: 'drop-shadow(-2px -2px 2px rgba(0,0,0,0.1))'
              }}></div>
              
              <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-4 flex items-center" style={{ fontFamily: 'Segoe Print, Comic Sans MS, sans-serif' }}>
                {/* Santa hat icon */}
                <span className="mr-2 inline-block" style={{ position: 'relative', width: '24px', height: '24px' }}>
                  {/* Hat body - red triangle */}
                  <span style={{
                    position: 'absolute',
                    width: 0,
                    height: 0,
                    borderLeft: '12px solid transparent',
                    borderRight: '12px solid transparent',
                    borderBottom: '20px solid #dc2626',
                    top: '0',
                    left: '0'
                  }}></span>
                  {/* White trim */}
                  <span style={{
                    position: 'absolute',
                    width: '24px',
                    height: '4px',
                    backgroundColor: 'white',
                    bottom: '4px',
                    left: '0',
                    borderRadius: '2px'
                  }}></span>
                  {/* Pom-pom */}
                  <span style={{
                    position: 'absolute',
                    width: '6px',
                    height: '6px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    top: '-2px',
                    left: '9px'
                  }}></span>
                </span>
                Elf Advisory Council Notices
              </h2>
              <div className="space-y-2.5 text-xs sm:text-sm text-gray-800" style={{ fontFamily: 'Segoe Print, Comic Sans MS, sans-serif', lineHeight: '1.6' }}>
                <p className="flex items-start">
                  <span className="mr-2 mt-0.5 font-bold">•</span>
                  <span>Please book a time slot for your arrival to the queue.</span>
                </p>
                <p className="flex items-start">
                  <span className="mr-2 mt-0.5 font-bold">•</span>
                  <span>A time slot is not required but we recommend one so that everyone does not arrive at the same time and the queue moves smoothly.</span>
                </p>
                <p className="flex items-start">
                  <span className="mr-2 mt-0.5 font-bold">•</span>
                  <span><strong>This time does not guarantee entry.</strong></span>
                </p>
                <p className="flex items-start">
                  <span className="mr-2 mt-0.5 font-bold">•</span>
                  <span>Please allow for time to park and walk to the event.</span>
                </p>
                <p className="flex items-start">
                  <span className="mr-2 mt-0.5 font-bold">•</span>
                  <span>We will allow for latecomers.</span>
                </p>
                <p className="flex items-start">
                  <span className="mr-2 mt-0.5 font-bold">•</span>
                  <span>If you have additional needs and cannot queue, please let us know at the event and bring your disability parking pass, AsIam card, or similar with you.</span>
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition"
              />
            </div>

            <div>
              <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-3">
                Select Your Time Slot
                <span className="block text-xs sm:text-sm font-normal text-gray-500 mt-1">
                  15-minute visits • Tap any slot to book
                </span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[60vh] sm:max-h-96 overflow-y-auto p-2 sm:p-3 border border-gray-200 rounded-lg bg-gray-50">
                {timeSlots.map((time) => {
                  const count = slotCounts[time] || 0;
                  const status = getSlotStatus(count);
                  
                  // Convert 24-hour to 12-hour format for display
                  const [hour, minute] = time.split(':');
                  const hourNum = parseInt(hour);
                  const ampm = hourNum >= 12 ? 'PM' : 'AM';
                  const displayHour = hourNum > 12 ? hourNum - 12 : hourNum === 0 ? 12 : hourNum;
                  const displayTime = `${displayHour}:${minute} ${ampm}`;
                  
                  return (
                    <button
                      key={time}
                      type="button"
                      onClick={() => setSelectedTime(time)}
                      className={`py-3 sm:py-4 px-2 rounded-lg font-medium transition text-sm sm:text-base relative touch-manipulation ${
                        selectedTime === time
                          ? `${status.bgClass} text-white ring-2 sm:ring-4 ${status.borderClass} ring-offset-2`
                          : `${status.bgClass} text-white ${status.hoverClass} active:scale-95`
                      }`}
                    >
                      <div className="font-semibold text-sm sm:text-base">{displayTime}</div>
                      <div className="text-xs font-bold mt-0.5 sm:mt-1">{status.label}</div>
                      <div className="text-xs opacity-90 mt-0.5">({count})</div>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs sm:text-sm text-gray-500 mt-2 text-center">
                🟢 Light (under 20) • 🟠 Busier (20-50) • 🔴 Busy (50+)
              </p>
            </div>

            {message.text && (
              <div
                className={`p-3 sm:p-4 rounded-lg text-sm sm:text-base ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}
              >
                {message.text}
              </div>
            )}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !email || !selectedTime}
              className="w-full bg-red-600 text-white py-3 sm:py-4 px-6 rounded-lg text-base sm:text-lg font-semibold hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition shadow-lg hover:shadow-xl active:scale-98 touch-manipulation"
            >
              {loading ? '🎅 Processing...' : isChangingBooking ? '🔄 Change My Santa Visit' : '🎁 Book My Santa Visit'}
            </button>
          </div>

          <div className="mt-4 sm:mt-6 text-center text-xs sm:text-sm text-gray-500 px-2">
            🎄 You'll receive a confirmation email with your Santa visit details 🎄
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;